import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { ICONS } from '../lib/icons';
import IconPicker from './IconPicker';

const { FiMenu, FiTrash2, FiEye, FiEyeOff, FiEdit2, FiCheck, FiX } = FiIcons;

export default function DraggableLinkItem({ link, onDelete, onToggleVisibility, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(link.title);
  const [editUrl, setEditUrl] = useState(link.url);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState(link.icon_name || 'Link');

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: link.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const handleSave = () => {
    let formattedUrl = editUrl.trim();
    if (formattedUrl && !formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
      formattedUrl = `https://${formattedUrl}`;
    }
    onUpdate(link.id, { title: editTitle, url: formattedUrl, icon_name: selectedIcon });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditTitle(link.title);
    setEditUrl(link.url);
    setSelectedIcon(link.icon_name || 'Link');
    setIsEditing(false);
  };

  const handleIconSelect = (iconName) => {
    setSelectedIcon(iconName);
    setShowIconPicker(false);
  };

  const IconComponent = ICONS[selectedIcon] || ICONS.Link;

  return (
    <>
      <div ref={setNodeRef} style={style} className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 transition-all hover:shadow-md">
        {isEditing ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <button onClick={() => setShowIconPicker(true)} className="p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors shrink-0">
                <SafeIcon icon={IconComponent} size={20} className="text-blue-600" />
              </button>
              <input type="text" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" placeholder="Link Title" />
            </div>
            <input type="text" value={editUrl} onChange={(e) => setEditUrl(e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" placeholder="URL" />
            <div className="flex gap-2">
              <button onClick={handleSave} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors">
                <SafeIcon icon={FiCheck} size={16} /> Save
              </button>
              <button onClick={handleCancel} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors">
                <SafeIcon icon={FiX} size={16} /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 overflow-hidden flex-1">
              <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600 shrink-0">
                <SafeIcon icon={FiMenu} size={20} />
              </button>
              <div className="bg-blue-50 p-2 rounded-lg text-blue-600 shrink-0">
                <SafeIcon icon={IconComponent} size={20} />
              </div>
              <div className="overflow-hidden flex-1">
                <h3 className="font-semibold text-gray-800 truncate">{link.title}</h3>
                <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 truncate hover:text-blue-500 block">
                  {link.url}
                </a>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => setIsEditing(true)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit link">
                <SafeIcon icon={FiEdit2} size={18} />
              </button>
              <button onClick={() => onToggleVisibility(link.id, link.is_visible)} className={`p-2 rounded-lg transition-colors ${link.is_visible ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'}`} title={link.is_visible ? 'Hide link' : 'Show link'}>
                <SafeIcon icon={link.is_visible ? FiEye : FiEyeOff} size={18} />
              </button>
              <button onClick={() => onDelete(link.id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Delete link">
                <SafeIcon icon={FiTrash2} size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
      {showIconPicker && (
        <IconPicker selectedIcon={selectedIcon} onSelect={handleIconSelect} onClose={() => setShowIconPicker(false)} />
      )}
    </>
  );
}